import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useScrollMotion from '../../hooks/useScrollMotion';
import m from '../Investors/brief-marketing.module.css';
import styles from '../Investors/Investors.module.css';
import b from '../Investors/Brief.module.css';
import { Bullets, DataTable, PartLabel, SectionHead, SectionNav, StatTiles, ValueList, useInView } from '../Investors/parts';

/*
 * VDS · StackCombo commercial brief, at /investors.
 * Copy and figures are the August 2026 sales and BD reference.
 * Statas is a separate company. It provides payment and loyalty.
 */

const IR_MAIL = 'mailto:invest@vendingdatasystems.co';
const DATA_ROOM = 'https://vendingdatasystems.co/dataroom';

const NAV: Array<{ id: string; text: string; group: string }> = [
  { id: 'platforms', text: 'Structure', group: 'Brief' },
  { id: 'formats', text: 'Formats', group: 'Brief' },
  { id: 'market', text: 'Market', group: 'Brief' },
  { id: 'gap', text: 'The gap', group: 'Opportunity' },
  { id: 'advantage', text: 'Advantage', group: 'Opportunity' },
  { id: 'hardware', text: 'Hardware', group: 'Opportunity' },
  { id: 'app', text: 'Resident app', group: 'Opportunity' },
  { id: 'daily', text: 'Daily revenue', group: 'Model' },
  { id: 'unit', text: 'One unit', group: 'Model' },
  { id: 'utilization', text: 'Utilization', group: 'Model' },
  { id: 'tiers', text: 'Tiers', group: 'Model' },
  { id: 'team', text: 'Team', group: 'Company' },
  { id: 'raise', text: 'The raise', group: 'Company' },
];

const HERO_STATS = [
  { value: '7,220', label: 'Units by month 60' },
  { value: '$30.25', label: 'Blended daily revenue per unit' },
  { value: '$11,041', label: 'Annual revenue per laundry unit' },
  { value: '24 mo', label: 'Base-case payback' },
];

const MARKET_BARS = [
  { year: '2026', label: '$780M', value: 780 },
  { year: '2027', label: '$920M', value: 920 },
  { year: '2028', label: '$1.08B', value: 1080 },
  { year: '2029', label: '$1.27B', value: 1270 },
  { year: '2030', label: '$1.49B', value: 1490 },
];
const MARKET_TOP = 1490;

const DAILY_HEAD = { format: 'Format', ticket: 'Avg ticket', tx: 'Tx / day', daily: 'Daily' };
const DAILY_ROWS = [
  { format: 'Food & beverage', ticket: '$1.80', tx: '22', daily: '$39.60' },
  { format: 'Coffee bar', ticket: '$2.40', tx: '28', daily: '$67.20' },
  { format: 'Lockers (per bay)', ticket: '$3.00', tx: '8', daily: '$24.00' },
  { format: 'Bar taps', ticket: '$4.50', tx: '14', daily: '$63.00' },
  { format: 'Sleep pod (per pod)', ticket: '$28.00', tx: '2.4', daily: '$67.20' },
  { format: 'Laundry (stacked)', ticket: '$2.10', tx: '14', daily: '$30.25' },
];
const DAILY_TOTAL = { format: 'Blended fleet average', ticket: '', tx: '', daily: '$30.25' };

const UTIL_HEAD = { scenario: 'Scenario', cycles: 'Cycles / day', util: 'Utilization', payback: 'Payback' };
const UTIL_ROWS = [
  { scenario: 'Base case', cycles: '7 + 7', util: '17%', payback: '24 mo' },
  { scenario: 'Modest ramp', cycles: '10 + 10', util: '24%', payback: '16 mo' },
  { scenario: 'Mature site', cycles: '14 + 14', util: '34%', payback: '12 mo' },
  { scenario: 'Stretch', cycles: '20 + 20', util: '49%', payback: '8 mo' },
];

const FORMATS = [
  { n: '01', name: 'Food & beverage', body: 'AI-optimized snack and beverage sales with intelligent inventory and predictive restocking.' },
  { n: '02', name: 'Coffee bar', body: 'Automated stations with intelligent brewing — personalized drinks, optimized inventory.' },
  { n: '03', name: 'Lockers', body: 'Secure storage with intelligent operations, predictive maintenance and seamless payment.' },
  { n: '04', name: 'Bar systems', body: 'Self-serve beverage dispensing with strategic tap placement, optimized pouring and smart payments.' },
  { n: '05', name: 'Sleep pods', body: 'Intelligent booking and predictive maintenance — smart scheduling and seamless payment.' },
  { n: '06', name: 'Laundry', body: 'Extra-large stacked washer-dryers with 24/7 ground-floor access — bedding, comforters, oversized loads.' },
];

const PILLARS = [
  { title: 'One platform', body: 'Shared payments, inventory AI, telemetry and ops — whatever the unit dispenses.' },
  { title: 'One install crew', body: 'Cross-trained on every format. A new site is a mix of categories matched to demand.' },
  { title: 'One host contract', body: 'A 30% revenue share covers any combination of machines deployed at the site.' },
];

const ADVANTAGE = [
  { title: 'Size matters', body: 'The X-Large 23kg drum handles oversized bedding and several loads in a single cycle — far beyond what home machines can take.' },
  { title: 'Clean beddings', body: 'King duvets, comforters, quilts, pillows and blankets fit the X-Large drum. Bulky bedding home machines cannot take.' },
  { title: 'On-site convenience', body: 'A full bedding-size washer and dryer in the building lets residents wash duvets, comforters and bulky bedding without a laundromat trip.' },
  { title: 'Save money', body: 'Residents stop paying a service to wash bulky bedding. They run multiple loads in one cycle and earn points as they go.' },
  { title: 'Healthier and cleaner', body: 'An all-metal commercial build resists bacteria, mold and mildew, and lets residents wash bedding far more often.' },
  { title: 'Eco-friendly', body: 'High-efficiency commercial cycles use dramatically less water and energy per load than home machines.' },
];

const HARDWARE = [
  'All-metal build resists bacteria and corrosion',
  'Higher wash temperatures kill pathogens and allergens',
  'Heavy-duty motors for faster, deeper cleans',
  'Anti-mold moisture management',
];

const SCREENS = [
  { title: 'Home', body: 'Live machine status, time remaining, and a push notification when the cycle ends.' },
  { title: 'Start a wash', body: 'Cycle, temperature and extras on one screen, then paid by credit, wallet or PromptPay.' },
  { title: 'Wallet', body: 'Prepaid cash balance, wash and dry credits, loyalty points and one-tap top-up.' },
  { title: 'Bundles', body: 'Residents mix wash, dry and credit packs. Larger blocks discount the per-cycle price.' },
];

const TIERS = [
  {
    tag: 'Tier 1 · Base case',
    where: 'Residential, dorms, offices, mid-market',
    value: '$8,252',
    note: 'Per-unit annual revenue before location share.',
    rows: [
      { label: 'Avg ticket', value: '$1.80–$3.00' },
      { label: 'Tx / day', value: '~14 blended' },
      { label: 'Categories on-site', value: 'F&B + coffee' },
      { label: 'Mix assumption', value: '80% of fleet' },
    ],
    equiv: 'StackCombo equivalent: wash $1.78, dry $2.37, dry extra time $0.30, at 7 + 7 + 4 cycles per day.',
  },
  {
    tag: 'Tier 2 · Upside',
    where: 'Hotels, transit, hospitals, serviced apartments, premium',
    value: '$10,424',
    note: '+26% versus Tier 1 on higher-ticket categories and foot traffic.',
    rows: [
      { label: 'Avg ticket', value: '$2.40–$28.00' },
      { label: 'Tx / day', value: '~18 blended' },
      { label: 'Categories on-site', value: 'Coffee + bar + pod' },
      { label: 'Mix assumption', value: '20% of fleet' },
    ],
    equiv: 'StackCombo equivalent: wash $2.00, dry $3.00, dry extra time $0.30.',
  },
];

const PEOPLE = [
  {
    name: 'Santi Suthisak',
    role: 'Founder & Chief Executive Officer',
    photo: '/images/santiceo.jpeg',
    bio: '20+ years. Business consultant and technology strategist leading digital transformation and cross-border innovation across Southeast Asia, spanning tourism, hospitality, infrastructure, energy, telecom and smart cities.',
  },
  {
    name: 'Marcus Gobel',
    role: 'Co-founder & Chief Technology Officer',
    photo: '/images/marcus.jpg',
    bio: '20+ years building secure, high-volume payment infrastructure. Senior roles at Shift4 and Wells Fargo; architected systems supporting $210B+ in annual payment volume for two top-10 global financial institutions. Payment and compliance consultant for Fortune 500 companies.',
  },
  {
    name: 'Matteo Argiolas',
    role: 'Director of Business Development',
    photo: '',
    bio: '9+ years. Bangkok-based leader in business development, digital product management and go-to-market strategy. Built and scaled financial platforms, launched new lending products and led cross-functional teams across Southeast Asia and Europe.',
  },
];

const TEAM_STATS = [
  { value: '50+', label: 'Years of combined fintech and platform leadership' },
  { value: '$210B+', label: 'Annual payment volume on systems architected in-house' },
  { value: '2', label: 'Regions built in: Southeast Asia and Europe' },
];

const ENTERPRISE = [
  'Built the incident-management platform at Wells Fargo — 1 in 7 global cross-border transactions',
  'Payment systems handling $210B+ annual volume at 99.99% uptime',
  'Led automation for two top-10 global financial institutions',
];

const GTM = [
  'Digital transformation and cross-border programs across Southeast Asia — tourism, hospitality, infrastructure, telecom and smart cities',
  'Built and scaled financial platforms and launched new lending products in Southeast Asia and Europe',
  'Payment and compliance advisory for Fortune 500 companies',
];

const TRACK = ['Shift4', 'Wells Fargo', 'Fortune 500 payment and compliance', 'Southeast Asia digital infrastructure'];

const CAPITAL = [
  {
    tag: 'Initial investment',
    amount: '$5.0M',
    body: 'Funds the first 200 units and operating expense through month 3, before the loan facility activates.',
  },
  {
    tag: 'Operating expense',
    amount: '$2.0M/yr',
    body: 'Steady-state from month 4. $100K a month in the ramp quarter while the team is hired.',
  },
  {
    tag: 'Hardware facility',
    amount: '5.0% · 10 yr',
    body: '$10K per unit drawn at install. 1% origination. Self-collateralized by the fleet.',
  },
];

const USE_OF_FUNDS = [
  { label: 'First 200 units · 40%', value: '$2.0M' },
  { label: 'Install and ops team · 25%', value: '$1.25M' },
  { label: 'Platform and AI · 20%', value: '$1.0M' },
  { label: 'G&A and legal · 10%', value: '$0.5M' },
  { label: 'Reserve · 5%', value: '$0.25M' },
];

const UNIT_FUNDING = [
  { label: 'Loan draw at install', value: '$10K' },
  { label: 'What the draw buys', value: 'Equipment, install, delivery' },
  { label: 'Repayment', value: '~$106 / month' },
  { label: 'Revenue-share payback', value: 'Month 24' },
];

const FIGURES = [
  'Market sizing. The ASEAN smart-vending forecast is a directional aggregation of published Statista, Mordor Intelligence, MarketsandMarkets and Allied Market Research ASEAN and APAC figures (2024–2026), plus Tao Bin disclosures. Country splits are internally estimated.',
  'Source documents. VDS2026 (0715), the Vending Data Systems investor briefing, 13 pages, is the primary source for the platforms, the daily-revenue table, the placement tiers and the capital plan. The StackCombo briefing, 17 slides, is the primary source for the one-unit year, utilization and the milestone set.',
  'Laundry daily revenue is printed as $30.25. The cycle build sums to that figure: wash $1.78 × 7 = $12.46, dry $2.37 × 7 = $16.59, dry extra time $0.30 × 4 = $1.20. The same row’s $2.10 average ticket times 14 transactions is $29.40. The page keeps the printed $30.25.',
  'The laundry year is the StackCombo model: $11,041 gross, a location-share line of ($2,476) labeled 30%, and $8,565 retained. Those three numbers foot. $2,476 is 30% of the Tier 1 annual ($8,252), not 30% of $11,041. The placement tiers, $8,252 and $10,424, are the VDS format-mix annuals before location share. They come from the other source deck and are not a second statement of the laundry year.',
  'About $106 a month is the payment on a $10,000 draw at 5% over 10 years. Month 24 is when revenue share has covered the deployed cost. It is not the maturity of the loan.',
  'Payment and loyalty services are provided by Statas, a separate company. The figures are in the companion Statas / OneAsia business reference and are not restated here. Third-party market data is cited as it appears in the source decks: World Bank, ASEAN Secretariat, Statista, Mordor Intelligence, MarketsandMarkets, Allied Market Research.',
];

const initials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2);

const MarketChart: React.FC = () => {
  const [ref, seen] = useInView<HTMLDivElement>(0.3);

  return (
    <div ref={ref} className={`${styles.chartCard} ${seen ? b.isOn : ''}`}>
      <div className={b.chartTop}>
        <span className={styles.compareLabel}>ASEAN smart-vending market size, USD</span>
      </div>
      <div className={b.colChart}>
        {MARKET_BARS.map((bar, i) => (
          <div key={bar.year} className={b.colItem} role="img" aria-label={`${bar.year}: ${bar.label}`}>
            <span className={b.colValue}>{bar.label}</span>
            <div className={b.colTrack}>
              <div
                className={`${b.colBar} ${i === MARKET_BARS.length - 1 ? b.colBarLast : ''}`}
                style={{ '--h': `${(bar.value / MARKET_TOP) * 100}%`, '--d': `${i * 120}ms` } as React.CSSProperties}
              />
            </div>
            <span className={b.colYear}>{bar.year}</span>
          </div>
        ))}
      </div>
      <p className={b.chartNote}>Year on year: +18%, +17%, +17%, +17%. Blended CAGR about 17%.</p>
    </div>
  );
};

const InvestorsVds: React.FC = () => {
  const { i18n } = useTranslation();
  const rootRef = useScrollMotion<HTMLDivElement>();

  useEffect(() => {
    const previous = i18n.language;
    if (previous?.substring(0, 2) !== 'en') void i18n.changeLanguage('en');
    return () => {
      if (previous && previous.substring(0, 2) !== 'en') void i18n.changeLanguage(previous);
    };
  }, [i18n]);

  useEffect(() => {
    const openAll = () => document.querySelectorAll('details').forEach((d) => (d.open = true));
    window.addEventListener('beforeprint', openAll);
    return () => window.removeEventListener('beforeprint', openAll);
  }, []);

  return (
    <div className={`${m.page} ${styles.investors} ${b.sectionNavPage} ${b.sectionNavWide}`} ref={rootRef}>
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={`${m.eyebrow} ${styles.heroEyebrow}`}>VDS · StackCombo · Confidential brief</div>
          <h1 className={`${styles.heroTitle} ${m.rise}`}>
            The commercial case
            <br />
            <span className={styles.heroAccent}>for the vending network.</span>
          </h1>
          <p className={`${styles.heroLead} ${m.rise} ${m.riseDelay1}`}>
            Market, operating problem, unit economics, the five-year fleet, and the capital plan. VDS operates one fleet across six formats. StackCombo is the laundry format and the most conservative case in the model. Statas is a separate company and provides payment and loyalty.
          </p>
          <div className={`${m.btnRow} ${m.btnRowCenter} ${m.rise} ${m.riseDelay2}`}>
            <a href={IR_MAIL} className={`${m.btn} ${m.btnLg} ${m.btnLight}`}>
              Talk to investor relations
            </a>
            <a href="#raise" className={`${m.btn} ${m.btnLg} ${m.btnGhostLight}`}>
              See the raise
            </a>
          </div>
        </div>
        <div className={styles.heroStats}>
          {HERO_STATS.map((stat, i) => (
            <div key={stat.label} className={styles.heroStat} data-reveal="true" data-reveal-delay={i * 90}>
              <div className={styles.heroStatValue}>{stat.value}</div>
              <div className={styles.heroStatLabel}>{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      <div className={b.banner} role="note">
        <strong>Confidential</strong> Audience: sales and business development, and qualified investors for the capital plan. Compiled August 2026. All figures USD. Company estimates and third-party data. Do not copy or share any part of this page without the written permission of Vending Data Systems.
      </div>
      <SectionNav
        label="Sections"
        prevLabel="Scroll sections left"
        nextLabel="Scroll sections right"
        items={NAV}
        rows
        appendix={{ id: 'appendix', text: 'Appendix' }}
      />

      <section id="platforms" className={m.section}>
        <div className={m.container}>
          <PartLabel
            index="01"
            title="Structure"
            body="Payment and loyalty, fleet operations, and the laundry format used in the model."
          />
          <SectionHead
            eyebrow="Overview"
            title="Roles"
            lead="VDS operates the hardware as one fleet. StackCombo is the laundry format. Statas is a separate company and provides payment and loyalty."
          />
          <div className={`${m.cardGrid} ${m.cols3}`}>
            <div className={m.card} data-reveal="true">
              <div className={b.tag}>Statas</div>
              <h3>Payment and loyalty</h3>
              <p>Statas provides the resident wallet, prepaid credits, loyalty points, and bundle pricing. It also runs offers and campaigns that support utilization on installed machines.</p>
            </div>
            <div className={m.card} data-reveal="true" data-reveal-delay={90}>
              <div className={b.tag}>VDS</div>
              <h3>Vending Data Systems</h3>
              <p>Intelligent cashless vending hardware for condos, dorms, hotels, serviced apartments, offices and transit hubs, run as one fleet on a single operations platform: placement, predictive maintenance and live monitoring. Six formats: food and beverage, coffee bar, lockers, bar taps, sleep pods, laundry.</p>
            </div>
            <div className={m.card} data-reveal="true" data-reveal-delay={180}>
              <div className={b.tag}>StackCombo</div>
              <h3>Stay on-site. Laundry 24/7.</h3>
              <p>The laundry format within VDS, and the model’s most conservative case. Extra-large commercial stacked washer-dryers on the ground floor of residential and hospitality buildings: full bedding, comforters and oversized loads in a single cycle, steps from the door. 23kg drum. 17% base-case utilization.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="formats" className={`${m.section} ${m.tint}`}>
        <div className={m.container}>
          <SectionHead
            eyebrow="1.1 · Six formats"
            title="One platform, any mix on site"
            lead="Any mix of formats can be deployed per location, matched to the demand on site."
          />
          <div className={`${m.cardGrid} ${m.cols3}`}>
            {FORMATS.map((format, i) => (
              <div key={format.n} className={m.card} data-reveal="true" data-reveal-delay={(i % 3) * 80}>
                <div className={b.tag}>{format.n}</div>
                <h3>{format.name}</h3>
                <p>{format.body}</p>
              </div>
            ))}
          </div>
          <div className={`${m.cardGrid} ${m.cols3} ${b.gapTop}`}>
            {PILLARS.map((pillar) => (
              <div key={pillar.title} className={`${m.card} ${b.compactCard}`}>
                <h3>{pillar.title}</h3>
                <p>{pillar.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="market" className={m.section}>
        <div className={m.container}>
          <PartLabel
            index="02"
            title="The market"
            body="Exports are cooling and domestic demand is carrying growth. The ASEAN smart-vending market nearly doubles by 2030."
          />
          <SectionHead
            eyebrow="2.1 · ASEAN smart vending"
            title="About 17% a year, to $1.49B"
            lead="A directional blend of published ASEAN and APAC forecasts. Country splits are internal estimates."
            split
          />
          <MarketChart />
          <div className={`${m.cardGrid} ${m.cols2} ${b.gapTop}`}>
            <div className={m.card}>
              <div className={b.cardValue}>14–18%</div>
              <h3>APAC intelligent vending</h3>
              <p>Recent projections put intelligent vending in Asia Pacific on a 14–18% CAGR. Southeast Asia outpaces that average on urbanisation.</p>
            </div>
            <div className={m.card}>
              <div className={b.cardValue}>30–60%+</div>
              <h3>Mature-site utilization</h3>
              <p>Well-located buildings run at 30–60% or higher. That range is the comparison for a VDS placement, not the laundry base case.</p>
            </div>
          </div>
          <p className={styles.sourceLine}>
            Sources: directional aggregation of Statista, Mordor Intelligence, MarketsandMarkets and Allied Market Research ASEAN/APAC smart-vending forecasts (2024–2026); Tao Bin disclosures.
          </p>
        </div>
      </section>

      <section id="gap" className={`${m.section} ${m.tint}`}>
        <div className={m.container}>
          <PartLabel
            index="03"
            title="The opportunity"
            body="The demand already exists inside the buildings. What is missing is on-site, 24/7, cashless access."
          />
          <SectionHead
            eyebrow="3.1 · The convenience gap"
            title="People already leave the building for this"
            lead="The vending thesis rests on demand that already exists inside residential and hospitality buildings."
          />
          <div className={`${m.cardGrid} ${m.cols2}`}>
            <div className={m.card}>
              <div className={b.tag}>Laundry, specifically</div>
              <h3>Home machines cannot finish the bedding</h3>
              <p>Home machines cannot reach the heat levels or dry times needed for bedding, comforters, duvets, blankets and curtains. Hauling those loads out of the building is the alternative.</p>
            </div>
            <div className={m.card}>
              <div className={b.tag}>Everything else</div>
              <h3>Cashless access where people gather</h3>
              <p>Residential buildings, offices, transit, hospitals, gyms, malls and schools. Mature, well-located buildings run at 30–60% or higher utilization.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="advantage" className={m.section}>
        <div className={m.container}>
          <SectionHead
            eyebrow="3.2 · StackCombo"
            title="X-Large laundry, happier tenants"
            lead="The resident-facing case for the laundry format, as it is sold on site."
          />
          <div className={`${m.cardGrid} ${m.cols3}`}>
            {ADVANTAGE.map((item, i) => (
              <div key={item.title} className={m.card} data-reveal="true" data-reveal-delay={(i % 3) * 80}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
          <p className={b.callout}>Multi-language. Mobile payment. Laundry made easy. Built and managed by laundry professionals.</p>
        </div>
      </section>

      <section id="hardware" className={`${m.section} ${m.tint}`}>
        <div className={m.container}>
          <SectionHead
            eyebrow="3.3 · The build"
            title="Commercial-grade, inside and out"
            lead="A hundred years of laundry engineering sits behind the hardware. The build is what makes the 17% base case safe to underwrite: machines that survive continuous unattended use in a residential lobby."
            split
          />
          <div className={b.twoCol}>
            <Bullets items={HARDWARE} />
            <div className={m.card}>
              <div className={b.tag}>Scan and wash</div>
              <h3>No coins, no quarters</h3>
              <p>Residents start, pay and track every cycle from their phone — tap, card or in-app.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="app" className={m.section}>
        <div className={m.container}>
          <SectionHead
            eyebrow="3.4 · Payment and loyalty"
            title="Resident application"
            lead="Statas provides the application residents use to start and pay for a cycle. It includes a prepaid wallet, credit balances, and bundle pricing. Residents can purchase credits before use. That advance purchase is the utilization upside in the model."
          />
          <div className={`${m.cardGrid} ${m.cols2}`}>
            {SCREENS.map((screen, i) => (
              <div key={screen.title} className={m.card} data-reveal="true" data-reveal-delay={(i % 2) * 80}>
                <div className={b.tag}>{screen.title}</div>
                <p>{screen.body}</p>
              </div>
            ))}
          </div>
          <div className={`${m.card} ${b.gapTop}`}>
            <div className={b.tag}>Scan to start</div>
            <h3>The machine’s own QR opens that unit</h3>
            <p>Each machine carries its own QR code. Scanning opens the cycle screen for that unit, so no keypad, coin mechanism or card reader is needed on the hardware. Every start, payment and fault is logged against the machine ID. Prepaid credit balances sit in front of the machine: residents arrive with washes already bought, which keeps repeat usage independent of a payment step at the door.</p>
          </div>
        </div>
      </section>

      <section id="daily" className={`${m.section} ${m.tint}`}>
        <div className={m.container}>
          <PartLabel
            index="04"
            title="The model"
            body="Every operating figure the brief prints, from daily revenue per unit to one laundry year."
          />
          <SectionHead
            eyebrow="4.1 · By format"
            title="Average daily revenue per unit"
            lead="The five-year model is built on the stacked washer-dryer, the most conservative format. Coffee, bar, sleep pods and lockers carry higher tickets in the same site."
          />
          <DataTable head={DAILY_HEAD} rows={DAILY_ROWS} total={DAILY_TOTAL} numeric={['ticket', 'tx', 'daily']} />
          <p className={b.chartNote}>
            The StackCombo model breaks the same $30.25 laundry day into its cycles: wash $1.78 × 7 = $12.46, dry $2.37 × 7 = $16.59, dry extra time $0.30 × 4 = $1.20. $30.25 × 365 is the $11,041 laundry year. The laundry row’s $2.10 ticket times 14 transactions is $29.40; the brief prints $30.25, and that is the figure used below.
          </p>
        </div>
      </section>

      <section id="unit" className={m.section}>
        <div className={m.container}>
          <SectionHead
            eyebrow="4.2 · One laundry unit"
            title="$11,041 in, $8,565 kept"
            lead="Twenty-four month payback. From revenue share alone, every unit pays back its deployed cost inside two years."
          />
          <div className={b.twoCol}>
            <div className={m.card}>
              <div className={b.tag}>One year</div>
              <ValueList
                rows={[
                  { label: 'Gross revenue', value: '$11,041' },
                  { label: 'Location share to host (30%)', value: '($2,476)' },
                ]}
                total={{ label: 'Net revenue retained', value: '$8,565' }}
              />
            </div>
            <div className={m.card}>
              <div className={b.tag}>Payback</div>
              <div className={b.cardValue}>24 months</div>
              <p>Superior large-capacity design plus ground-floor 24/7 access is what the brief credits for usage: residents who finally have the laundry they were leaving the building to do.</p>
            </div>
          </div>
          <p className={b.chartNote}>
            The three printed figures foot: $11,041 minus $2,476 is $8,565. The $2,476 is not 30% of that gross. Thirty percent of $11,041 is about $3,312. $2,476 is 30% of the Tier 1 annual, $8,252. The page keeps the line as the brief prints it.
          </p>
        </div>
      </section>

      <section id="utilization" className={`${m.section} ${m.tint}`}>
        <div className={m.container}>
          <SectionHead
            eyebrow="4.3 · Headroom"
            title="The base case is 17%"
            lead="Seven wash cycles plus seven dry cycles a day. The brief treats that as light usage, with room above it."
          />
          <DataTable head={UTIL_HEAD} rows={UTIL_ROWS} numeric={['cycles', 'util', 'payback']} />
          <div className={`${b.twoCol} ${b.gapTop}`}>
            <div className={m.card}>
              <div className={b.tag}>Key insight</div>
              <h3>The bear case is not low utilization</h3>
              <p>The model already assumes very light usage. The real opportunity is execution: signing locations and driving adoption.</p>
            </div>
            <div className={m.card}>
              <div className={b.tag}>Benchmark</div>
              <h3>Laundromats run hotter</h3>
              <p>Comparable laundromats operate at 40–60% utilization. A mature VDS site in the table above is 34%. Stretch is 49%.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="tiers" className={m.section}>
        <div className={m.container}>
          <SectionHead
            eyebrow="4.4 · Placement"
            title="Same hardware, two site tiers"
            lead="Two tiers with built-in upside: same hardware, same footprint, higher margin in premium sites."
          />
          <div className={b.twoCol}>
            {TIERS.map((tier) => (
              <div key={tier.tag} className={m.card}>
                <div className={b.tag}>{tier.tag}</div>
                <p>{tier.where}</p>
                <div className={b.cardValue}>{tier.value}</div>
                <p>{tier.note}</p>
                <ValueList rows={tier.rows} />
                <p className={b.chartNote}>{tier.equiv}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="team" className={`${m.section} ${m.tint}`}>
        <div className={m.container}>
          <PartLabel
            index="05"
            title="The company"
            body="Operators who have done it before, then the capital plan the source brief holds in its appendix."
          />
          <SectionHead
            eyebrow="Team"
            title="Fifty years, three operators"
            lead="Combined leadership across payments, compliance, enterprise platforms and go-to-market. This brief names three people."
            split
          />
          <div className={b.people}>
            {PEOPLE.map((person, i) => (
              <div key={person.name} className={b.person} data-reveal="true" data-reveal-delay={i * 80}>
                <div className={b.personTop}>
                  {person.photo ? (
                    <img className={b.avatar} src={person.photo} alt="" loading="lazy" />
                  ) : (
                    <span className={`${b.avatar} ${b.avatarInitials}`} aria-hidden="true">
                      {initials(person.name)}
                    </span>
                  )}
                  <div>
                    <h3>{person.name}</h3>
                    <div className={b.personRole}>{person.role}</div>
                  </div>
                </div>
                <p>{person.bio}</p>
              </div>
            ))}
          </div>
          <div className={b.gapTop}>
            <StatTiles items={TEAM_STATS} cols={3} />
          </div>
          <div className={`${b.twoCol} ${b.gapTop}`}>
            <div>
              <div className={styles.compareLabel}>Enterprise platform</div>
              <Bullets items={ENTERPRISE} />
            </div>
            <div>
              <div className={styles.compareLabel}>Market and go-to-market</div>
              <Bullets items={GTM} />
            </div>
          </div>
          <div className={styles.compareLabel}>Track record</div>
          <ul className={b.chips}>
            {TRACK.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <section id="raise" className={`${m.section} ${m.navy}`}>
        <div className={m.container}>
          <SectionHead
            eyebrow="Appendix A · Capital plan"
            title="$5M equity opens a self-funding network"
            lead="Useful background for BD conversations, and written for investors. Check before sharing it outside that audience."
            navy
            split
          />
          <div className={b.stages}>
            {CAPITAL.map((item, i) => (
              <div key={item.tag} className={`${m.glassCard} ${b.stage} ${i === 0 ? b.stageFirst : ''}`} data-reveal="true" data-reveal-delay={i * 90}>
                <span className={b.glassLabel}>{item.tag}</span>
                <div className={b.stageAmount}>{item.amount}</div>
                <p className={b.glassBody}>{item.body}</p>
              </div>
            ))}
          </div>
          <div className={`${b.twoCol} ${b.gapTop}`}>
            <div className={m.card}>
              <div className={b.tag}>Use of the $5M</div>
              <ValueList rows={USE_OF_FUNDS} total={{ label: 'Equity', value: '$5.0M' }} />
            </div>
            <div className={m.card}>
              <div className={b.tag}>How one unit gets funded</div>
              <ValueList rows={UNIT_FUNDING} />
              <p className={b.chartNote}>
                About $106 a month is the payment on a $10,000 draw at 5% over the 10-year facility. Month 24 is the revenue-share payback from the laundry year above, not the date the loan ends.
              </p>
            </div>
          </div>
          <p className={b.callout}>
            The ask is a Series A to fund the first 200-unit deployment, hire the install and ops team, and activate the hardware credit facility. The term sheet is open through Q1 2027. First close is targeted for Q1 2027.
          </p>
          <div className={`${m.btnRow} ${b.gapTop}`}>
            <a href={IR_MAIL} className={`${m.btn} ${m.btnLg} ${m.btnLight}`}>
              invest@vendingdatasystems.co
            </a>
            <a href={DATA_ROOM} className={`${m.btn} ${m.btnLg} ${m.btnGhostLight}`}>
              Open the data room
            </a>
          </div>
        </div>
      </section>

      <section id="appendix" className={m.section}>
        <div className={m.container}>
          <SectionHead
            eyebrow="Appendix B"
            title="Sources and figure notes"
            lead="Where the decks disagree, the source brief uses the later figure and records the earlier one. These notes keep both visible."
          />
          <ol className={b.figureNotes}>
            {FIGURES.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ol>
        </div>
      </section>

      <section id="contact" className={`${m.section} ${m.tint}`}>
        <div className={styles.cta}>
          <h2 className={m.displayHead}>Contact investor relations</h2>
          <p className={m.lead}>The data room and the underlying decks are available to qualified investors. The capital plan is investor material. Check before it leaves that audience.</p>
          <div className={`${m.btnRow} ${m.btnRowCenter}`}>
            <a href={IR_MAIL} className={`${m.btn} ${m.btnLg} ${m.btnPrimary}`}>
              Contact investor relations
            </a>
            <a href={DATA_ROOM} className={`${m.btn} ${m.btnLg} ${m.btnOutline} ${m.btnOutlineOnTint}`}>
              Open the data room
            </a>
          </div>
        </div>
        <div className={`${m.container} ${styles.sources}`}>
          <p className={styles.disclaimer}>
            Compiled August 2026 from the VDS and StackCombo business reference. The 7,220 units are the month-60 case in that brief, not a count of machines in the field. Market sizes are directional third-party aggregates. Forward-looking targets are not guarantees of future performance. Nothing on this page is an offer of securities.
          </p>
        </div>
      </section>
    </div>
  );
};

export default InvestorsVds;
